/**
 * ParsedQuestionsPreview Component
 * 
 * Displays AI-parsed questions in editable form for user review and modification
 */

import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Save, X, AlertCircle, CheckCircle, Image as ImageIcon, ZoomIn, Trash2, ToggleLeft, Upload, Plus, GripVertical, Loader2 } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Button from '../Button';
import QuestionImageUpload from './QuestionImageUpload';
import { ParsedQuestion } from './types';

const API_URL = import.meta.env.VITE_API_URL || '';

/** Sortable wrapper for vertical text-choice rows. Shows a grip handle. */
function SortableChoiceItem({ id, children, disabled }: { id: string; children: React.ReactNode; disabled?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({ id, disabled });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: 'transform 0ms',
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="flex items-center gap-1">
        {!disabled && (
          <button type="button" {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 flex-shrink-0 p-0.5">
            <GripVertical className="w-3.5 h-3.5" />
          </button>
        )}
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
}

/** Sortable wrapper for image-tile grids. Entire tile is draggable (no side grip handle). */
function SortableTileItem({ id, children, disabled }: { id: string; children: React.ReactNode; disabled?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({ id, disabled });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: 'transform 0ms',
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
    cursor: disabled ? undefined : 'grab',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...(disabled ? {} : listeners)}>
      {children}
    </div>
  );
}

interface ParsedQuestionsPreviewProps {
  parsedQuestions: ParsedQuestion[];
  onQuestionsUpdated: (questions: ParsedQuestion[]) => void;
  onUseQuestions?: (questions: ParsedQuestion[]) => void;
  onGoBack?: () => void;
  onStartSimulation?: (questions: ParsedQuestion[]) => Promise<void>;
  isLoading?: boolean;
  className?: string;
  saveMode?: boolean;
}

const QUESTION_TYPES = [
  { value: 'single_choice', label: 'Single Choice (Radio)' },
  { value: 'multiple_choice', label: 'Multiple Choice (Checkboxes)' },
  { value: 'yes_no', label: 'Yes/No' },
  { value: 'rating_scale', label: 'Rating Scale' },
  { value: 'likert_scale', label: 'Likert Scale' },
  { value: 'ranking', label: 'Ranking' },
  { value: 'short_text', label: 'Short Text' },
  { value: 'long_text', label: 'Long Text' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'matrix_grid', label: 'Matrix Grid' },
  { value: 'matrix_rating', label: 'Matrix Rating' },
  { value: 'nps', label: 'Net Promoter Score' },
  { value: 'numeric_input', label: 'Numeric Input' },
  { value: 'max_diff', label: 'MaxDiff (Best-Worst Scaling)' },
  { value: 'conjoint_analysis', label: 'Conjoint Analysis' },
];

// Image Modal Component
interface ImageModalProps {
  imageUrl: string;
  description?: string;
  onClose: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({ imageUrl, description, onClose }) => {
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-5xl max-h-[95vh] bg-white rounded-lg overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 bg-white rounded-full p-2 hover:bg-gray-100 transition-colors z-10 shadow-lg"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="overflow-auto max-h-[95vh]">
          <img
            src={imageUrl}
            alt="Full size"
            className="w-full object-contain"
          />
          {description && (
            <div className="p-6 bg-gray-50 border-t">
              <div className="flex items-start mb-2">
                <AlertCircle className="w-4 h-4 text-blue-500 mr-2 mt-0.5" />
                <h4 className="text-sm font-semibold text-gray-800">AI Description</h4>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {description}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ParsedQuestionsPreview: React.FC<ParsedQuestionsPreviewProps> = ({
  parsedQuestions,
  onQuestionsUpdated,
  isLoading = false,
  className = '',
}) => {
  const [questions, setQuestions] = useState<ParsedQuestion[]>(parsedQuestions);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [viewingImage, setViewingImage] = useState<{url: string, description?: string} | null>(null);
  const [originalQuestionState, setOriginalQuestionState] = useState<ParsedQuestion | null>(null);
  const [imageUploadExpandedFor, setImageUploadExpandedFor] = useState<string | null>(null);
  const [uploadingOptionImage, setUploadingOptionImage] = useState<string | null>(null);

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    setQuestions(prev => {
      if (parsedQuestions.length === 0) return parsedQuestions;
      if (prev.length === 0) return parsedQuestions;

      const localById = new Map(prev.map(q => [q.id, q]));
      return parsedQuestions.map(incoming => {
        const local = localById.get(incoming.id);
        if (!local) return incoming;

        // Preserve user-intent edits (type, images) from local state,
        // but ALWAYS accept parsed-from-text fields (choices, text)
        // from the incoming re-parse — the textarea is the source of truth.
        const hasLocalImages = (local.image_paths?.length ?? 0) > 0;
        const hasLocalOptionImages = local.option_images && Object.keys(local.option_images).length > 0;

        return {
          ...incoming,
          type: local.type,
          image_paths: hasLocalImages ? local.image_paths : incoming.image_paths,
          image_descriptions: hasLocalImages ? local.image_descriptions : incoming.image_descriptions,
          image_attachment_level: local.image_attachment_level ?? incoming.image_attachment_level,
          option_images: hasLocalOptionImages ? local.option_images : incoming.option_images,
          scale_min: local.scale_min ?? incoming.scale_min,
          scale_max: local.scale_max ?? incoming.scale_max,
        };
      });
    });
  }, [parsedQuestions]);

  // info_screen questions are kept internally but hidden from the preview
  // since AI simulation doesn't use them
  const displayQuestions = questions.filter(q => q.type !== 'info_screen');

  const updateQuestion = (questionId: string, updates: Partial<ParsedQuestion>) => {
    const updatedQuestions = questions.map(q => 
      q.id === questionId ? { ...q, ...updates } : q
    );
    setQuestions(updatedQuestions);

    onQuestionsUpdated(updatedQuestions);
  };

  const addChoice = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      const choices = question.choices || [];
      updateQuestion(questionId, {
        choices: [...choices, `Option ${choices.length + 1}`]
      });
    }
  };

  const removeChoice = (questionId: string, choiceIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.choices) {
      const choices = question.choices.filter((_, index) => index !== choiceIndex);
      updateQuestion(questionId, { choices });
    }
  };

  const updateChoice = (questionId: string, choiceIndex: number, value: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.choices) {
      const oldLabel = question.choices[choiceIndex];
      const choices = [...question.choices];
      choices[choiceIndex] = value;

      const updates: Partial<ParsedQuestion> = { choices };
      if (question.option_images && oldLabel in question.option_images) {
        const oi = { ...question.option_images };
        oi[value] = oi[oldLabel];
        delete oi[oldLabel];
        updates.option_images = oi;
      }
      updateQuestion(questionId, updates);
    }
  };

  const reorderChoice = (questionId: string, fromIndex: number, toIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (!question || !question.choices) return;
    const choices = [...question.choices];
    const [moved] = choices.splice(fromIndex, 1);
    choices.splice(toIndex, 0, moved);
    updateQuestion(questionId, { choices });
  };

  const removeQuestion = (questionId: string) => {
    const updatedQuestions = questions.filter(q => q.id !== questionId);
    setQuestions(updatedQuestions);

    onQuestionsUpdated(updatedQuestions);
    // Close edit mode if deleting the currently edited question
    if (editingQuestionId === questionId) {
      setEditingQuestionId(null);
    }
  };

  const handleImageUploadInEdit = (questionId: string, imageData: {path: string, url: string, file: File}) => {
    // Simply add image path to question - descriptions will be generated in batch on backend
    setQuestions(prevQuestions => {
      const updated = prevQuestions.map(q => {
        if (q.id === questionId) {
          return {
            ...q,
            image_paths: [...(q.image_paths || []), imageData.url]
          };
        }
        return q;
      });
      onQuestionsUpdated(updated);
      return updated;
    });

  };

  const handleImageRemoveInEdit = (questionId: string, imageIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.image_paths) {
      const updatedPaths = question.image_paths.filter((_, idx) => idx !== imageIndex);
      const updatedDescriptions = question.image_descriptions 
        ? question.image_descriptions.filter((_, idx) => idx !== imageIndex)
        : [];
      
      // If option-level attachment, also update choices and option_images
      let updates: Partial<ParsedQuestion> = {
        image_paths: updatedPaths,
        image_descriptions: updatedDescriptions
      };
      
      if (question.option_images && Object.keys(question.option_images).length > 0 && question.choices) {
        // Remove corresponding choice and option_image entry
        const updatedChoices = [...question.choices];
        const removedChoice = updatedChoices[imageIndex];
        updatedChoices.splice(imageIndex, 1);
        
        const updatedOptionImages = { ...question.option_images };
        delete updatedOptionImages[removedChoice];
        
        updates.choices = updatedChoices;
        updates.option_images = updatedOptionImages;
      }
      
      // Update paths and descriptions (remove both at same index)
      updateQuestion(questionId, updates);
    }
  };

  const handleAttachmentLevelToggle = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    const currentLevel = question.image_attachment_level || 'question';
    const newLevel = currentLevel === 'question' ? 'option' : 'question';

    if (newLevel === 'option') {
      // Convert to option-level: create choices from images
      const imagePaths = question.image_paths || [];
      const newChoices = imagePaths.map((_, idx) => `Image ${idx + 1}`);
      const newOptionImages: Record<string, string> = {};
      newChoices.forEach((choice, idx) => {
        if (imagePaths[idx]) {
          newOptionImages[choice] = imagePaths[idx];
        }
      });
      
      updateQuestion(questionId, {
        image_attachment_level: 'option',
        choices: newChoices,
        option_images: newOptionImages,
        type: question.type === 'single_choice' || question.type === 'multiple_choice' ? question.type : 'single_choice'
      });
    } else {
      // Convert to question-level: keep images, remove option_images mapping
      updateQuestion(questionId, {
        image_attachment_level: 'question',
        option_images: undefined
      });
    }
  };

  const handleOptionImageUpload = async (questionId: string, optionIndex: number, imageData: string) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    const uploadKey = `${questionId}-${optionIndex}`;
    setUploadingOptionImage(uploadKey);
    
    try {
      const response = await fetch(imageData);
      const blob = await response.blob();
      const file = new File([blob], `option_${optionIndex}_${Date.now()}.jpg`, { type: 'image/jpeg' });
      
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResponse = await fetch(`${API_URL}/api/question-images/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      const data = await uploadResponse.json();
      
      const choiceLabel = question.choices?.[optionIndex];
      if (choiceLabel && data.image_url) {
        updateQuestion(questionId, {
          option_images: {
            ...question.option_images,
            [choiceLabel]: data.image_url
          }
        });
      }
    } catch (error) {
      console.error('Failed to upload option image:', error);
    } finally {
      setUploadingOptionImage(null);
    }
  };

  const handleOptionImageRemove = (questionId: string, optionIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (!question || !question.choices) return;
    
    const choiceLabel = question.choices[optionIndex];
    const updatedOptionImages = { ...question.option_images };
    delete updatedOptionImages[choiceLabel];
    
    updateQuestion(questionId, {
      option_images: updatedOptionImages
    });
  };

  const hasChoices = (type: string) => {
    return ['single_choice', 'multiple_choice', 'dropdown', 'ranking'].includes(type);
  };

  const hasScale = (type: string) => {
    return ['rating_scale', 'likert_scale', 'nps'].includes(type);
  };

  const hasMaxDiff = (type: string) => {
    return type === 'max_diff';
  };

  const hasConjoint = (type: string) => {
    return type === 'conjoint_analysis';
  };

  const hasMatrix = (type: string) => {
    return ['matrix_rating', 'matrix_grid'].includes(type);
  };

  const addMatrixRow = (questionId: string) => {
    const question = parsedQuestions.find(q => q.id === questionId);
    if (!question) return;
    const rows = question.matrix_rows || [];
    updateQuestion(questionId, {
      matrix_rows: [...rows, `Item ${rows.length + 1}`]
    });
  };

  const removeMatrixRow = (questionId: string, rowIndex: number) => {
    const question = parsedQuestions.find(q => q.id === questionId);
    if (question && question.matrix_rows) {
      const rows = question.matrix_rows.filter((_, index) => index !== rowIndex);
      updateQuestion(questionId, { matrix_rows: rows });
    }
  };

  const updateMatrixRow = (questionId: string, rowIndex: number, value: string) => {
    const question = parsedQuestions.find(q => q.id === questionId);
    if (question && question.matrix_rows) {
      const rows = [...question.matrix_rows];
      rows[rowIndex] = value;
      updateQuestion(questionId, { matrix_rows: rows });
    }
  };

  const addMaxDiffItem = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      const items = question.maxdiff_items || [];
      updateQuestion(questionId, {
        maxdiff_items: [...items, `Item ${items.length + 1}`]
      });
    }
  };

  const removeMaxDiffItem = (questionId: string, itemIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.maxdiff_items) {
      const items = question.maxdiff_items.filter((_, index) => index !== itemIndex);
      updateQuestion(questionId, { maxdiff_items: items });
    }
  };

  const updateMaxDiffItem = (questionId: string, itemIndex: number, value: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.maxdiff_items) {
      const items = [...question.maxdiff_items];
      items[itemIndex] = value;
      updateQuestion(questionId, { maxdiff_items: items });
    }
  };

  const addConjointAttribute = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      const attributes = question.conjoint_attributes || [];
      updateQuestion(questionId, {
        conjoint_attributes: [...attributes, { name: `Attribute ${attributes.length + 1}`, levels: ['Level 1', 'Level 2'] }]
      });
    }
  };

  const removeConjointAttribute = (questionId: string, attrIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = question.conjoint_attributes.filter((_, index) => index !== attrIndex);
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const updateConjointAttributeName = (questionId: string, attrIndex: number, name: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = [...question.conjoint_attributes];
      attributes[attrIndex] = { ...attributes[attrIndex], name };
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const addConjointLevel = (questionId: string, attrIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = [...question.conjoint_attributes];
      const levels = attributes[attrIndex].levels || [];
      attributes[attrIndex] = {
        ...attributes[attrIndex],
        levels: [...levels, `Level ${levels.length + 1}`]
      };
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const removeConjointLevel = (questionId: string, attrIndex: number, levelIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = [...question.conjoint_attributes];
      const levels = attributes[attrIndex].levels.filter((_, index) => index !== levelIndex);
      attributes[attrIndex] = { ...attributes[attrIndex], levels };
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const updateConjointLevel = (questionId: string, attrIndex: number, levelIndex: number, value: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = [...question.conjoint_attributes];
      const levels = [...attributes[attrIndex].levels];
      levels[levelIndex] = value;
      attributes[attrIndex] = { ...attributes[attrIndex], levels };
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };


  if (isLoading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-3"></div>
          <span className="text-gray-600">Processing parsed questions...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm p-6 ${className}`}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle className="w-5 h-5 text-blue-500" />
          <h2 className="text-lg font-semibold text-gray-900">
            Review & Edit Questions
          </h2>
        </div>
        <p className="text-sm text-gray-500">
          Click any question to edit its text, change the type, or attach images.
        </p>
        
        {/* Show image summary if any questions have images */}
        {(() => {
          const questionsWithImages = displayQuestions.filter(q => q.image_paths && q.image_paths.length > 0);
          const totalImages = displayQuestions.reduce((sum, q) => sum + (q.image_paths?.length || 0), 0);
          
          if (totalImages > 0) {
            return (
              <div className="mt-3 flex items-center text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-md">
                <ImageIcon className="w-4 h-4 mr-2" />
                <span>
                  {totalImages} {totalImages === 1 ? 'image' : 'images'} across {questionsWithImages.length} {questionsWithImages.length === 1 ? 'question' : 'questions'}
                </span>
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Questions List */}
      <div className="space-y-4 mb-6">
        {displayQuestions.map((question, index) => (
          <div
            key={question.id}
            className={`border rounded-lg transition-all ${
              editingQuestionId === question.id
                ? 'border-blue-300 bg-white shadow-sm ring-1 ring-blue-100'
                : 'border-gray-200 bg-white hover:border-blue-200 hover:shadow-sm cursor-pointer'
            }`}
            onClick={() => {
              if (editingQuestionId !== question.id && editingQuestionId === null) {
                setOriginalQuestionState(JSON.parse(JSON.stringify(question)));
                setEditingQuestionId(question.id);
              }
            }}
          >
            {/* Question Header */}
            <div className="flex items-start justify-between p-4 pb-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
                  {index + 1}
                  </span>
                    <select
                      value={question.type}
                  onChange={(e) => {
                    e.stopPropagation();
                    updateQuestion(question.id, { type: e.target.value });
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs font-medium px-2 py-1 rounded-full border-0 bg-blue-50 text-blue-700 cursor-pointer hover:bg-blue-100 transition-colors focus:ring-1 focus:ring-blue-300 focus:outline-none appearance-none"
                  style={{ paddingRight: '1.5rem', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%231d4ed8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.4rem center' }}
                    >
                      {QUESTION_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
              </div>
              
              <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                {editingQuestionId === question.id ? (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingQuestionId(null); setImageUploadExpandedFor(null); }}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                    >
                      <Save className="w-3.5 h-3.5" />
                      Done
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (originalQuestionState) {
                          setQuestions(prevQuestions => 
                            prevQuestions.map(q => 
                              q.id === originalQuestionState.id ? originalQuestionState : q
                            )
                          );
                          setOriginalQuestionState(null);
                        }
                        setEditingQuestionId(null);
                        setImageUploadExpandedFor(null);
                    
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); removeQuestion(question.id); }}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    data-tooltip="Delete question"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Question Text */}
            <div className="px-4 pb-3">
              {editingQuestionId === question.id ? (
                <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                  <textarea
                    value={question.text}
                    onChange={(e) => updateQuestion(question.id, { text: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none text-sm leading-relaxed focus:ring-1 focus:ring-blue-300 focus:border-blue-300 focus:outline-none"
                    rows={3}
                    placeholder="Enter question text (markdown supported)"
                    autoFocus
                  />

                  {/* Image section in edit mode — question-level images (context) */}
                  {(() => {
                    const hasImages = question.image_paths && question.image_paths.length > 0;
                    const hasOptionImages = question.option_images && Object.keys(question.option_images).length > 0;
                    const aiDetectedImages = hasImages || hasOptionImages;
                    const showUpload = hasImages || aiDetectedImages || imageUploadExpandedFor === question.id;

                    if (!showUpload) {
                      return (
                        <button
                          onClick={() => setImageUploadExpandedFor(question.id)}
                          className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          data-tooltip="Add context image for this question (shown to all personas)"
                        >
                          <ImageIcon className="w-3.5 h-3.5" />
                          Attach question image
                        </button>
                      );
                    }

                    return (
                      <div>
                        {aiDetectedImages && !hasImages && (
                          <div className="flex items-center gap-1.5 mb-1.5 px-1">
                            <ImageIcon className="w-3.5 h-3.5 text-blue-500" />
                            <span className="text-xs text-blue-600 font-medium">
                              AI detected this question needs images
                            </span>
                          </div>
                        )}
                          <QuestionImageUpload
                            questionIndex={index}
                          onImageUpload={(_, imageData) => {
                            handleImageUploadInEdit(question.id, imageData);
                            if (!aiDetectedImages) setImageUploadExpandedFor(null);
                          }}
                            onImageRemove={(_, imageIndex) => handleImageRemoveInEdit(question.id, imageIndex)}
                            existingImageUrls={question.image_paths || []}
                            maxImages={5}
                          />
                        {(hasImages || aiDetectedImages) && (
                          <div className="mt-2 p-2.5 bg-gray-50 rounded-md border border-gray-200">
                          <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-600">
                                {(question.option_images && Object.keys(question.option_images).length > 0)
                                  ? 'Option images attached'
                                  : 'Images provide context'}
                              </p>
                            <button
                              onClick={() => handleAttachmentLevelToggle(question.id)}
                                className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded transition-colors bg-blue-50 text-blue-700 hover:bg-blue-100"
                              >
                                <ToggleLeft className="w-3.5 h-3.5" />
                                {(question.option_images && Object.keys(question.option_images).length > 0) ? 'Switch to Context' : 'Switch to Options'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })()}
                  </div>
                ) : (
                <div className="prose-sm max-w-none text-gray-800 leading-relaxed">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ children }) => <p className="text-sm text-gray-800 mb-1 last:mb-0 leading-relaxed">{children}</p>,
                      strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                      em: ({ children }) => <em className="italic text-gray-600">{children}</em>,
                      ul: ({ children }) => <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5 my-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal list-inside text-sm text-gray-700 space-y-0.5 my-1">{children}</ol>,
                      li: ({ children }) => <li className="text-sm text-gray-700">{children}</li>,
                      code: ({ children }) => <code className="text-xs bg-gray-100 px-1 py-0.5 rounded font-mono text-gray-800">{children}</code>,
                      a: ({ children, href }) => <a href={href} className="text-blue-600 hover:text-blue-700 underline">{children}</a>,
                      blockquote: ({ children }) => <blockquote className="border-l-2 border-blue-200 pl-3 my-1 text-sm text-gray-600 italic">{children}</blockquote>,
                      h1: ({ children }) => <h1 className="text-base font-semibold text-gray-900 mb-1">{children}</h1>,
                      h2: ({ children }) => <h2 className="text-sm font-semibold text-gray-900 mb-1">{children}</h2>,
                      h3: ({ children }) => <h3 className="text-sm font-medium text-gray-900 mb-0.5">{children}</h3>,
                    }}
                  >
                    {question.text}
                  </ReactMarkdown>
                </div>
              )}
            </div>

            {/* Question-Level Images Section - Show in VIEW mode (visible even with option images) */}
            {editingQuestionId !== question.id && (
              <div className="px-4 pb-3" onClick={(e) => e.stopPropagation()}>
                {question.image_paths && question.image_paths.length > 0 ? (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <ImageIcon className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs font-medium text-gray-500">
                        {question.image_paths.length} {question.image_paths.length === 1 ? 'image' : 'images'} (context)
                        </span>
                      </div>
                    <div className="flex flex-wrap gap-2">
                      {question.image_paths.map((imagePath, imgIndex) => {
                        const fullImageUrl = imagePath.startsWith('http') ? imagePath : `${API_URL}${imagePath}`;
                        const imageDescription = question.image_descriptions?.[imgIndex];
                        
                        return (
                          <div key={imgIndex} className="relative group">
                            <div 
                              className="relative cursor-pointer"
                              onClick={() => setViewingImage({url: fullImageUrl, description: imageDescription})}
                            >
                              <img
                                src={fullImageUrl}
                                alt={`Q${index + 1} image ${imgIndex + 1}`}
                                className="w-20 h-20 object-cover rounded-md border border-gray-200 hover:border-blue-300 transition-colors animate-img-pop-in"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100">
                                <ZoomIn className="w-4 h-4 text-white drop-shadow" />
                                </div>
                              </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleImageRemoveInEdit(question.id, imgIndex);
                              }}
                              className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                              data-tooltip="Remove image"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                      {(question.image_paths?.length || 0) < 5 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOriginalQuestionState(JSON.parse(JSON.stringify(question)));
                            setEditingQuestionId(question.id);
                            setImageUploadExpandedFor(question.id);
                          }}
                          className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center gap-1 hover:border-blue-400 hover:bg-gray-50 transition-colors cursor-pointer"
                          data-tooltip="Add context image for this question"
                        >
                          <Plus className="w-4 h-4 text-gray-400" />
                          <span className="text-[10px] text-gray-400">{question.image_paths!.length}/5</span>
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOriginalQuestionState(JSON.parse(JSON.stringify(question)));
                      setEditingQuestionId(question.id);
                      setImageUploadExpandedFor(question.id);
                    }}
                    className="inline-flex items-center gap-1.5 py-1 px-2 text-xs text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    data-tooltip="Add context image for this question (shown to all personas)"
                  >
                    <ImageIcon className="w-3.5 h-3.5" />
                    <span>Attach question image</span>
                  </button>
                )}
              </div>
            )}

            {/* Question Details */}
            <div className="px-4 pb-3 space-y-3" onClick={(e) => e.stopPropagation()}>
              {/* Choices — only show if choices were actually parsed or option-level images exist */}
              {hasChoices(question.type) && ((question.choices && question.choices.length > 0) || (question.option_images && Object.keys(question.option_images).length > 0)) && (
                <div>
                  <label
                    className={`block text-sm font-medium text-gray-700 mb-2${editingQuestionId !== question.id ? ' cursor-pointer' : ''}`}
                    onClick={editingQuestionId !== question.id ? () => {
                      setOriginalQuestionState(JSON.parse(JSON.stringify(question)));
                      setEditingQuestionId(question.id);
                    } : undefined}
                  >
                    Answer Choices
                    {question.option_images && Object.keys(question.option_images).length > 0 && (
                      <span className="ml-2 text-xs text-blue-600 font-normal">(with images)</span>
                    )}
                  </label>
                  {(() => {
                    const choices = question.choices ?? [];
                    return choices.length > 0
                      && !!question.option_images
                      && choices.every(c => question.option_images?.[c]);
                  })() ? (
                    <>
                      <DndContext
                        sensors={dndSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(event: DragEndEvent) => {
                          const { active, over } = event;
                          if (!over || active.id === over.id) return;
                          const fromIdx = (question.choices || []).findIndex((_, i) => `opt-${question.id}-${i}` === active.id);
                          const toIdx = (question.choices || []).findIndex((_, i) => `opt-${question.id}-${i}` === over.id);
                          if (fromIdx !== -1 && toIdx !== -1) reorderChoice(question.id, fromIdx, toIdx);
                        }}
                      >
                        <SortableContext
                          items={(question.choices || []).map((_, i) => `opt-${question.id}-${i}`)}
                          strategy={rectSortingStrategy}
                        >
                    <div className="flex flex-wrap gap-3 mt-2">
                      {(question.choices || []).map((choice, i) => {
                        const imageUrl = question.option_images?.[choice];
                        const fullImageUrl = imageUrl ? (imageUrl.startsWith('http') ? imageUrl : `${API_URL}${imageUrl}`) : null;
                        
                        return (
                          <SortableTileItem
                            key={`opt-${question.id}-${i}`}
                            id={`opt-${question.id}-${i}`}
                            disabled={editingQuestionId !== question.id}
                          >
                            <div className="relative group">
                              <div className="absolute top-1 left-1 bg-gray-900 bg-opacity-75 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center z-10">
                                {i + 1}
                              </div>
                              
                              {fullImageUrl ? (
                                <>
                                  <img
                                    src={fullImageUrl}
                                    alt={`Option ${i + 1}`}
                                    className="h-32 w-32 object-cover rounded border-2 border-gray-300 cursor-pointer hover:border-blue-400 transition-colors animate-img-pop-in"
                                    onClick={() => setViewingImage({url: fullImageUrl})}
                                  />
                                  <button
                                    onClick={() => handleOptionImageRemove(question.id, i)}
                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                    data-tooltip="Remove image"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <label className="h-32 w-32 flex flex-col items-center justify-center bg-blue-50 rounded border-2 border-dashed border-blue-300 cursor-pointer hover:border-blue-400 hover:bg-blue-100 transition-colors">
                                    <Upload className="w-6 h-6 text-blue-400 mb-1" />
                                    <span className="text-xs text-blue-500">Upload</span>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(e) => {
                                        if (e.target.files?.[0]) {
                                          const reader = new FileReader();
                                          reader.onload = () => {
                                            handleOptionImageUpload(question.id, i, reader.result as string);
                                          };
                                          reader.readAsDataURL(e.target.files[0]);
                                        }
                                      }}
                                    />
                                  </label>
                                  {editingQuestionId === question.id && (
                                    <button
                                      onClick={() => removeChoice(question.id, i)}
                                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                      data-tooltip="Remove option"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </>
                              )}
                              {editingQuestionId === question.id ? (
                                <input
                                  type="text"
                                  value={choice}
                                  onChange={(e) => updateChoice(question.id, i, e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-32 mt-1 px-2 py-1 text-xs border border-gray-300 rounded text-center truncate"
                                  placeholder={`Option ${i + 1}`}
                                />
                              ) : (
                                <p className="w-32 mt-1 text-xs text-gray-700 text-center truncate" title={choice}>{choice}</p>
                              )}
                            </div>
                          </SortableTileItem>
                        );
                      })}
                      
                            <div className="flex items-center justify-center" style={{ minHeight: '10rem' }}>
                        <button
                          onClick={() => addChoice(question.id)}
                                className="h-32 w-32 border-2 border-dashed border-gray-300 rounded hover:border-blue-400 flex flex-col items-center justify-center text-gray-500 transition-colors"
                        >
                          <Plus className="w-6 h-6 mb-1" />
                          <span className="text-xs">Add Option</span>
                        </button>
                      </div>
                    </div>
                        </SortableContext>
                      </DndContext>
                      <div className="mt-2 p-2.5 bg-gray-50 rounded-md border border-gray-200">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-600">Images are selectable options</p>
                          <button
                            onClick={() => handleAttachmentLevelToggle(question.id)}
                            className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded transition-colors bg-blue-50 text-blue-700 hover:bg-blue-100"
                          >
                            <ToggleLeft className="w-3.5 h-3.5" /> Switch to Context
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    // REGULAR VERTICAL LIST: For text choices or edit mode of non-option-level images
                    <DndContext
                      sensors={dndSensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event: DragEndEvent) => {
                        const { active, over } = event;
                        if (!over || active.id === over.id) return;
                        const fromIdx = (question.choices || []).findIndex((_, i) => `choice-${question.id}-${i}` === active.id);
                        const toIdx = (question.choices || []).findIndex((_, i) => `choice-${question.id}-${i}` === over.id);
                        if (fromIdx !== -1 && toIdx !== -1) reorderChoice(question.id, fromIdx, toIdx);
                      }}
                    >
                      <SortableContext
                        items={(question.choices || []).map((_, i) => `choice-${question.id}-${i}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div
                          className="space-y-2"
                          onClick={editingQuestionId !== question.id ? () => {
                            setOriginalQuestionState(JSON.parse(JSON.stringify(question)));
                            setEditingQuestionId(question.id);
                          } : undefined}
                          style={editingQuestionId !== question.id ? { cursor: 'pointer' } : undefined}
                        >
                      {(question.choices || []).map((choice, choiceIndex) => {
                        const optImg = question.option_images?.[choice];
                        const optImgUrl = optImg ? (optImg.startsWith('http') ? optImg : `${API_URL}${optImg}`) : null;
                        const isUploading = uploadingOptionImage === `${question.id}-${choiceIndex}`;
                        return (
                            <SortableChoiceItem
                              key={`choice-${question.id}-${choiceIndex}`}
                              id={`choice-${question.id}-${choiceIndex}`}
                              disabled={editingQuestionId !== question.id}
                            >
                              <div className="flex items-start gap-2">
                                <span className="text-sm text-gray-500 w-6 flex-shrink-0 mt-1">
                                  {choiceIndex + 1}.
                                </span>
                                {(optImgUrl || isUploading) && (
                                  <div className="relative group/img flex-shrink-0">
                                    {isUploading ? (
                                      <div className="w-24 h-24 rounded-md border-2 border-blue-300 bg-blue-50 flex items-center justify-center">
                                        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                                      </div>
                                    ) : (
                                      <>
                                        <img
                                          src={optImgUrl!}
                                          alt={choice}
                                          className="w-24 h-24 rounded-md object-cover border border-gray-200 cursor-pointer hover:border-blue-300 transition-colors animate-img-pop-in"
                                          onClick={() => setViewingImage({ url: optImgUrl! })}
                                        />
                                        {editingQuestionId === question.id && (
                                          <button
                                            onClick={() => handleOptionImageRemove(question.id, choiceIndex)}
                                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity shadow-sm"
                                            data-tooltip="Remove option image"
                                          >
                                            <X className="w-3 h-3" />
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                )}
                                {editingQuestionId === question.id ? (
                                  <div className="flex-1 min-w-0 flex items-center gap-1.5 mt-1">
                                    <input
                                      type="text"
                                      value={choice}
                                      onChange={(e) => updateChoice(question.id, choiceIndex, e.target.value)}
                                      className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                    />
                                    <label
                                      className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors cursor-pointer flex-shrink-0"
                                      data-tooltip={optImgUrl ? 'Replace option image' : 'Add image to this option'}
                                    >
                                      {optImgUrl ? <ImageIcon className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                          if (e.target.files?.[0]) {
                                            const reader = new FileReader();
                                            reader.onload = () => {
                                              handleOptionImageUpload(question.id, choiceIndex, reader.result as string);
                                            };
                                            reader.readAsDataURL(e.target.files[0]);
                                          }
                                        }}
                                      />
                                    </label>
                                    <button
                                      onClick={() => removeChoice(question.id, choiceIndex)}
                                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                                      data-tooltip="Remove this choice"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-900 mt-1">{choice}</span>
                                )}
                              </div>
                            </SortableChoiceItem>
                        );
                      })}
                      {editingQuestionId === question.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => addChoice(question.id)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          + Add Choice
                        </Button>
                      )}
                    </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              )}

              {/* Matrix rows for matrix_rating/matrix_grid questions */}
              {hasMatrix(question.type) && (
                <div>
                  <label
                    className={`block text-sm font-medium text-gray-700 mb-2${editingQuestionId !== question.id ? ' cursor-pointer' : ''}`}
                    onClick={editingQuestionId !== question.id ? () => {
                      setOriginalQuestionState(JSON.parse(JSON.stringify(question)));
                      setEditingQuestionId(question.id);
                    } : undefined}
                  >
                    Items to Rate
                  </label>
                  <div
                    className="space-y-2"
                    onClick={editingQuestionId !== question.id ? () => {
                      setOriginalQuestionState(JSON.parse(JSON.stringify(question)));
                      setEditingQuestionId(question.id);
                    } : undefined}
                    style={editingQuestionId !== question.id ? { cursor: 'pointer' } : undefined}
                  >
                    {(question.matrix_rows || []).map((row, rowIndex) => (
                      <div key={rowIndex} className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 w-6 flex-shrink-0">
                          {rowIndex + 1}.
                        </span>
                        {editingQuestionId === question.id ? (
                          <>
                            <input
                              type="text"
                              value={row}
                              onChange={(e) => updateMatrixRow(question.id, rowIndex, e.target.value)}
                              className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                              placeholder="Item to rate"
                            />
                            <button
                              onClick={() => removeMatrixRow(question.id, rowIndex)}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-sm text-gray-900">{row}</span>
                        )}
                      </div>
                    ))}
                    {editingQuestionId === question.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => addMatrixRow(question.id)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        + Add Item
                      </Button>
                    )}
                    {(!question.matrix_rows || question.matrix_rows.length === 0) && editingQuestionId !== question.id && (
                      <span className="text-sm text-gray-500 italic">No items defined</span>
                    )}
                  </div>
                </div>
              )}

              {/* Scale display for scale-based questions (always visible) */}
              {hasScale(question.type) && (
                <div>
                  {editingQuestionId === question.id ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Min Value
                        </label>
                        <input
                          type="number"
                          value={question.scale_min || 1}
                          onChange={(e) => updateQuestion(question.id, { scale_min: parseInt(e.target.value) })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Value
                        </label>
                        <input
                          type="number"
                          value={question.scale_max || 5}
                          onChange={(e) => updateQuestion(question.id, { scale_max: parseInt(e.target.value) })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium text-gray-700">Scale Range:</span>
                      <span className="text-sm text-gray-900">
                        {question.scale_min || 1} - {question.scale_max || (question.type === 'nps' ? 10 : 5)}
                      </span>
                      {question.type === 'likert_scale' && (
                        <span className="text-xs text-gray-600 italic">
                          (Strongly Disagree to Strongly Agree)
                        </span>
                      )}
                      {question.type === 'nps' && (
                        <span className="text-xs text-gray-600 italic">
                          (Not at all likely to Extremely likely)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Additional metadata display */}
              {question.type === 'multiple_choice' && question.max_selections && editingQuestionId !== question.id && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Max Selections:</span>
                  <span className="text-sm text-gray-900">{question.max_selections}</span>
                </div>
              )}

              {question.type === 'ranking' && question.ranking_count && editingQuestionId !== question.id && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Rank Top:</span>
                  <span className="text-sm text-gray-900">{question.ranking_count} choices</span>
                </div>
              )}

              {(question.type === 'short_text' || question.type === 'long_text') && question.text_limit && editingQuestionId !== question.id && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Character Limit:</span>
                  <span className="text-sm text-gray-900">{question.text_limit}</span>
                </div>
              )}

              {/* MaxDiff Items */}
              {hasMaxDiff(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    MaxDiff Items
                    {question.maxdiff_items && question.maxdiff_items.length > 0 && editingQuestionId !== question.id && (
                      <span className="ml-2 text-xs text-blue-600 font-normal">
                        ({question.maxdiff_items.length} items, 4 per set, {Math.max(3, Math.ceil(question.maxdiff_items.length / 4))} sets per persona)
                      </span>
                    )}
                  </label>
                  <div className="space-y-2">
                    {(question.maxdiff_items || []).map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500 w-6 flex-shrink-0">
                          {itemIndex + 1}.
                        </span>
                        {editingQuestionId === question.id ? (
                          <>
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => updateMaxDiffItem(question.id, itemIndex, e.target.value)}
                              className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                            />
                            <button
                              onClick={() => removeMaxDiffItem(question.id, itemIndex)}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                              data-tooltip="Remove item"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </>
                        ) : (
                          <span className="text-sm text-gray-900">{item}</span>
                        )}
                      </div>
                    ))}
                    {editingQuestionId === question.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => addMaxDiffItem(question.id)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        + Add Item
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Conjoint Attributes */}
              {hasConjoint(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Conjoint Attributes
                  </label>
                  {editingQuestionId === question.id && (
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Task Description
                      </label>
                      <textarea
                        value={question.conjoint_description || ''}
                        onChange={(e) => updateQuestion(question.id, { conjoint_description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        rows={2}
                        placeholder="Describe the choice task (e.g., 'Select your preferred smartphone')"
                      />
                    </div>
                  )}
                  <div className="space-y-4">
                    {(question.conjoint_attributes || []).map((attr, attrIndex) => (
                      <div key={attrIndex} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          {editingQuestionId === question.id ? (
                            <input
                              type="text"
                              value={attr.name}
                              onChange={(e) => updateConjointAttributeName(question.id, attrIndex, e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-medium mr-2"
                              placeholder="Attribute name"
                            />
                          ) : (
                            <span className="text-sm font-medium text-gray-800">{attr.name}</span>
                          )}
                          {editingQuestionId === question.id && (
                            <button
                              onClick={() => removeConjointAttribute(question.id, attrIndex)}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                              data-tooltip="Remove attribute"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-gray-600">Levels:</span>
                          <div className="flex flex-wrap gap-2">
                            {attr.levels.map((level, levelIndex) => (
                              <div key={levelIndex} className="flex items-center">
                                {editingQuestionId === question.id ? (
                                  <div className="flex items-center space-x-1">
                                    <input
                                      type="text"
                                      value={level}
                                      onChange={(e) => updateConjointLevel(question.id, attrIndex, levelIndex, e.target.value)}
                                      className="px-2 py-1 border border-gray-300 rounded text-xs w-24"
                                    />
                                    <button
                                      onClick={() => removeConjointLevel(question.id, attrIndex, levelIndex)}
                                      className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                                      data-tooltip="Remove level"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs">
                                    {level}
                                  </span>
                                )}
                              </div>
                            ))}
                            {editingQuestionId === question.id && (
                              <button
                                onClick={() => addConjointLevel(question.id, attrIndex)}
                                className="px-2 py-1 border border-dashed border-gray-300 rounded text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                              >
                                + Add Level
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {editingQuestionId === question.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => addConjointAttribute(question.id)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        + Add Attribute
                      </Button>
                    )}
                  </div>
                  {editingQuestionId !== question.id && question.conjoint_description && (
                    <div className="mt-2 text-xs text-gray-600 italic">
                      Task: {question.conjoint_description}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer hint */}
      <p className="pt-3 text-xs text-gray-400 text-center">
        {displayQuestions.length} question{displayQuestions.length !== 1 ? 's' : ''} &middot; click any card to edit
      </p>

      {/* Image Modal */}
      {viewingImage && (
        <ImageModal
          imageUrl={viewingImage.url}
          description={viewingImage.description}
          onClose={() => setViewingImage(null)}
        />
      )}
    </div>
  );
};

export default ParsedQuestionsPreview;